import auth from '@/auth/auth';
import { EditableCardStatus } from '@/components/Card/types';
import { useFluent } from 'fluent-vue';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

export const EDIT_MODES = ['Edit-Untouched', 'Edit-Touched'];

export interface ActiveCard {
    id: string;
    label: string;
    status: EditableCardStatus;
    hasErrors: boolean;
    processing: boolean;
}

const activeCard = ref({
    id: '',
    label: '',
    status: 'View' as EditableCardStatus,
    hasErrors: false,
    processing: false,
});

export function useActiveCard() {
    const fluent = useFluent();
    const isAdmin = auth.isAdmin();
    const router = useRouter();

    function updateActiveCard(
        id: string,
        label: string,
        status: EditableCardStatus,
        hasErrors: boolean,
        processing: boolean,
    ) {
        activeCard.value = { id, label, status, hasErrors, processing };
    }

    function updateProcessing(processing: boolean) {
        activeCard.value = { ...activeCard.value, processing };
    }

    function updateHasErrors(hasErrors: boolean) {
        activeCard.value = { ...activeCard.value, hasErrors };
    }

    function resetActiveCard() {
        activeCard.value = { id: '', label: '', status: 'View', hasErrors: false, processing: false };
    }

    function setCardStatus(status: EditableCardStatus) {
        activeCard.value = { ...activeCard.value, status };
    }

    function getCardStatus(id: string): EditableCardStatus {
        return activeCard.value.id === id ? activeCard.value.status : 'View';
    }

    function editButtonDisabledMessage(id: string) {
        if (!isAdmin) return fluent.$t('no-permission-to-edit');
        if (activeCard.value.id !== '' && activeCard.value.id !== id) return fluent.$t('other-card-is-editing');
        return '';
    }

    function saveButtonDisabledMessage(id: string) {
        if (!isAdmin) return fluent.$t('no-permission-to-edit');
        if (activeCard.value.id !== '' && activeCard.value.id !== id) return fluent.$t('other-card-is-editing');
        if (activeCard.value.processing === true) return fluent.$t('upload-in-progress');
        if (activeCard.value.hasErrors === true) return fluent.$t('unresolved-form-errors');
        return '';
    }

    function onEnterEditMode(id: string, label: string) {
        activeCard.value = { id, label, status: 'Edit-Untouched', hasErrors: false, processing: false };
    }

    function onLeaveEditMode() {
        resetActiveCard();
    }

    function showForm(id: string) {
        if (activeCard.value.id === id && EDIT_MODES.includes(activeCard.value.status)) {
            return true;
        }
        return false;
    }

    function getDisabledExplanation(tabRouteName: string) {
        return computed(() => {
            if (activeCard.value.id !== '' && activeCard.value.status !== 'View') {
                const navItemRouteName = router.currentRoute.value.name;
                if (navItemRouteName && navItemRouteName !== tabRouteName) {
                    return ref(fluent.$t('tabs-disabled', { name: activeCard.value.label }));
                }
            }
            return '';
        });
    }

    return {
        activeCard,
        updateActiveCard,
        updateProcessing,
        updateHasErrors,
        resetActiveCard,
        setCardStatus,
        getCardStatus,
        onEnterEditMode,
        onLeaveEditMode,
        showForm,
        editButtonDisabledMessage,
        saveButtonDisabledMessage,
        getDisabledExplanation,
    };
}
